import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Bell, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ScrollReveal,
  MagneticButton,
  FloatingElement,
} from '@/components/ui/OptimizedAnimations';
import {
  CharacterStagger,
  SparklesCore,
} from '@/components/ui/AceternityAnimations';

const pageInfo = {
  '/press': {
    title: 'Press Kit',
    description: 'Media resources, brand assets, and press releases coming soon.',
    icon: '📰',
  },
  '/docs': {
    title: 'Documentation',
    description: 'Comprehensive guides, API documentation, and tutorials coming soon.',
    icon: '📚',
  },
  '/blog': {
    title: 'Blog',
    description: 'Insights, updates, and stories from the Tattva Techworkz team coming soon.',
    icon: '✍️',
  },
  '/support': {
    title: 'Support Center',
    description: 'Help desk, knowledge base, and ticket system coming soon.',
    icon: '🛟',
  },
};

const ComingSoonPage = () => {
  const location = useLocation();
  const info = pageInfo[location.pathname] || {
    title: 'Coming Soon',
    description: 'This page is under construction.',
    icon: '🚀',
  };

  return (
    <div className="relative bg-void min-h-screen pt-20">
      <section className="relative py-24 overflow-hidden min-h-[80vh] flex items-center">
        <div className="absolute inset-0 z-[1]">
          <SparklesCore
            id="comingSoonSparkles"
            background="transparent"
            minSize={0.4}
            maxSize={1}
            particleDensity={20}
            particleColor="#00BFA5"
          />
        </div>

        <FloatingElement duration={8} distance={20} className="absolute top-20 right-10 opacity-10">
          <div className="w-64 h-64 border border-teal/30 rounded-full" />
        </FloatingElement>

        <FloatingElement duration={6} distance={15} className="absolute bottom-20 left-10 opacity-10">
          <div className="w-48 h-48 border border-orange/30 rounded-full" />
        </FloatingElement>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal delay={0.1}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-teal text-sm mb-8 hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.3 }}
              className="text-7xl mb-8"
            >
              {info.icon}
            </motion.div>
          </ScrollReveal>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold text-foreground mb-4">
            <CharacterStagger
              text={info.title}
              delay={0.4}
              staggerDelay={0.03}
            />
          </h1>

          <ScrollReveal delay={0.6}>
            <p className="text-muted-foreground text-lg sm:text-xl max-w-2xl mx-auto mb-8">
              {info.description}
            </p>
          </ScrollReveal>

          <ScrollReveal delay={0.8}>
            <div className="glass-card inline-flex items-center gap-3 px-6 py-4 mb-12">
              <Clock className="w-5 h-5 text-teal" />
              <span className="text-foreground font-medium">Launching Soon</span>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={1}>
            <p className="text-muted-foreground text-sm mb-6">
              Want to be notified when this goes live?
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <MagneticButton strength={0.2}>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-primary"
                  >
                    <Bell className="w-5 h-5" />
                    Notify Me
                  </motion.button>
                </Link>
              </MagneticButton>
              <MagneticButton strength={0.2}>
                <a href="mailto:info@tattvatechworkz.com">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-secondary"
                  >
                    <Mail className="w-5 h-5" />
                    Contact Us
                  </motion.button>
                </a>
              </MagneticButton>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default ComingSoonPage;
